import { useState, type FormEvent } from "react";
import { loginUser, setAuthToken, setUserData } from "../../utils/auth";
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleLoginButton from "./GoogleLoginButton";
import FacebookLoginButton from "./FacebookLoginButton";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (
    token: string,
    user: { id: string; email: string; name: string }
  ) => void;
  onSwitchToRegister: () => void;
  onForgotPassword: () => void;
  consultorName?: string;
}

export default function LoginModal({
  isOpen,
  onClose,
  onLogin,
  onSwitchToRegister,
  onForgotPassword,
  consultorName,
}: LoginModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const googleClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await loginUser(email, password);
      onLogin(response.token, response.user);
      setEmail("");
      setPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao fazer login");
    } finally {
      setIsLoading(false);
    }
  };

  async function handleGoogleSuccess(userData: { email: string; name: string; googleId: string }) {
    try {
      setIsLoading(true);
      setError(null);

      const res = await fetch("/api/users/googleAuth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userData.email,
          name: userData.name,
          googleId: userData.googleId,
        }),
      });

      if (!res.ok) {
        throw new Error("Erro ao autenticar com Google");
      }

      const data = await res.json();

      // Store token and user data in cookies
      setAuthToken(data.token);
      setUserData(data.user);

      // Call the parent handler
      // Note: handleLogin in LandingPage already closes the modal via setIsLoginModalOpen(false)
      // Do NOT call onClose() here — it clears selectedConsultor, breaking the chat start flow
      onLogin(data.token, data.user);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao autenticar com Google"
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function sendFacebookRegistration(facebookData: {
    profile: { email: string; name: string; id: string };
  }) {
    try {
      setIsLoading(true);
      setError(null);

      const res = await fetch("/api/users/facebookAuth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: facebookData.profile.email,
          name: facebookData.profile.name,
          facebookId: facebookData.profile.id,
        }),
      });

      if (!res.ok) {
        throw new Error("Erro ao autenticar com Facebook");
      }

      const data = await res.json();

      // Store token and user data in cookies
      setAuthToken(data.token);
      setUserData(data.user);

      // Call the parent handler
      // Note: handleLogin in LandingPage already closes the modal via setIsLoginModalOpen(false)
      // Do NOT call onClose() here — it clears selectedConsultor, breaking the chat start flow
      onLogin(data.token, data.user);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao autenticar com Facebook"
      );
    } finally {
      setIsLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-[rgba(2,6,23,0.95)] backdrop-blur-sm flex items-start sm:items-center justify-center z-[100] p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative max-w-[420px] w-full bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl p-5 sm:p-6 border border-[#1e293b] shadow-[0_20px_60px_rgba(0,0,0,0.9)] my-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-transparent border-none text-[#64748b] text-2xl cursor-pointer hover:text-[#e5e7eb] hover:bg-[rgba(148,163,184,0.1)] transition-all"
          aria-label="Fechar"
        >
          ×
        </button>

        <h3 className="text-2xl font-bold text-[#f1f5f9] mb-1 mt-0">
          Entre para começar
        </h3>
        <p className="text-[0.9rem] text-[#cbd5e1] mb-5">
          {consultorName
            ? `Para falar com ${consultorName}, aceda à sua conta.`
            : "Aceda à sua conta e tenha a sua leitura de Tarot imediatamente."}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg px-3 py-2 text-sm text-red-400">
              {error}
            </div>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#334155]"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-gradient-to-br from-[#0f172a] to-[#020617] px-2 text-[#64748b]">
                ou continue com
              </span>
            </div>
          </div>

          {googleClientId && (
            <GoogleOAuthProvider clientId={googleClientId}>
              <GoogleLoginButton
                onSuccess={handleGoogleSuccess}
                onError={() => setError("Erro ao autenticar com Google")}
                label="Entrar com Google"
                disabled={isLoading}
              />
            </GoogleOAuthProvider>
          )}
          <FacebookLoginButton
            appId="922024209965284"
            onSuccess={sendFacebookRegistration}
            label="Entrar com Facebook"
          />
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#334155]"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-gradient-to-br from-[#0f172a] to-[#020617] px-2 text-[#64748b]">
                ou use o email
              </span>
            </div>
          </div>

          <div>
            <label
              htmlFor="login-email"
              className="block text-[0.85rem] font-medium text-[#cbd5e1] mb-1.5"
            >
              E-mail
            </label>
            <input
              type="email"
              id="login-email"
              placeholder="o-seu-email@exemplo.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-[#334155] px-3.5 py-2.5 text-[0.95rem] bg-[#0f172a] text-[#f1f5f9] placeholder:text-[#64748b] outline-none focus:border-[#8b5cf6] focus:ring-2 focus:ring-[rgba(139,92,246,0.2)] transition-all"
            />
          </div>

          <div>
            <label
              htmlFor="login-password"
              className="block text-[0.85rem] font-medium text-[#cbd5e1] mb-1.5"
            >
              Palavra-passe
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="login-password"
                placeholder="A sua palavra-passe"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-[#334155] px-3.5 py-2.5 pr-11 text-[0.95rem] bg-[#0f172a] text-[#f1f5f9] placeholder:text-[#64748b] outline-none focus:border-[#8b5cf6] focus:ring-2 focus:ring-[rgba(139,92,246,0.2)] transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#cbd5e1] transition-colors bg-transparent border-none cursor-pointer p-1"
                aria-label={
                  showPassword
                    ? "Ocultar palavra-passe"
                    : "Mostrar palavra-passe"
                }
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-[0.8rem] text-[#a78bfa] hover:text-[#c4b5fd] hover:underline transition-colors bg-transparent border-none cursor-pointer"
            >
              Esqueceu a palavra-passe?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full inline-flex items-center justify-center rounded-xl px-4 py-3 text-[0.95rem] font-semibold bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] text-white shadow-[0_10px_30px_rgba(139,92,246,0.4)] hover:shadow-[0_15px_40px_rgba(139,92,246,0.5)] hover:translate-y-[-2px] transition-all mt-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {isLoading ? "A entrar..." : "Entrar"}
          </button>
        </form>

        <div className="mt-5 pt-5 border-t border-[#1e293b] text-center">
          <p className="text-[0.85rem] text-[#94a3b8]">
            Não tem conta?{" "}
            <button
              onClick={onSwitchToRegister}
              className="text-[#a78bfa] font-medium hover:text-[#c4b5fd] hover:underline transition-colors bg-transparent border-none cursor-pointer"
            >
              Registar
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
